import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

const formatYAxis = (v) => {
  if (v >= 1000000) return `${(v / 1000000)?.toFixed(1)}M`;
  if (v >= 1000) return `${(v / 1000)?.toFixed(0)}K`;
  return v;
};

const MonthlyEvolutionChart = ({ data, loading, serversData, selectedServer }) => {
  const { t } = useTranslation();
  const servers = useMemo(() => serversData || [], [serversData]);

  const monthsShort = t('admin.performance.monthsShort', { returnObjects: true, defaultValue: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] });

  const formatPeriod = (period) => {
    if (!period) return '';
    const [year, month] = period?.split('-');
    const m = parseInt(month) - 1;
    return `${monthsShort?.[m] || month} ${year?.slice(2)}`;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div
        className="rounded-lg px-3 py-2 text-xs"
        style={{ backgroundColor: '#1a1a1a', border: '1px solid #2f2f2f', fontFamily: 'Inter, sans-serif' }}
      >
        <p className="font-semibold mb-2" style={{ color: 'rgba(255,255,255,0.8)' }}>{formatPeriod(label)}</p>
        {payload?.map(entry => (
          <div key={entry?.dataKey} className="flex items-center gap-2 mb-0.5">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry?.color }} />
            <span style={{ color: 'rgba(255,255,255,0.5)' }}>{entry?.dataKey}:</span>
            <span style={{ color: entry?.color, fontWeight: 600 }}>{formatYAxis(entry?.value || 0)}</span>
          </div>
        ))}
      </div>
    );
  };

  const chartData = useMemo(() => {
    if (!data?.length) return [];
    // FIX A1-C: filter before aggregating if server selected
    const filteredData = selectedServer && selectedServer !== 'All'
      ? data?.filter(r => r?.server === selectedServer)
      : data;
    const aggregated = {};
    filteredData?.forEach(r => {
      if (!r?.period) return;
      if (!aggregated?.[r?.period]) aggregated[r.period] = { period: r?.period };
      if (!aggregated?.[r?.period]?.[r?.server]) aggregated[r.period][r.server] = 0;
      aggregated[r.period][r.server] += parseInt(r?.diamonds) || 0;
    });
    return Object.values(aggregated)?.sort((a, b) => a?.period?.localeCompare(b?.period));
  }, [data, selectedServer]);

  // Only show servers relevant to current filter
  const visibleServers = useMemo(() => {
    if (selectedServer && selectedServer !== 'All') {
      return servers?.filter(s => s?.id === selectedServer);
    }
    return servers;
  }, [servers, selectedServer]);

  if (loading || !chartData?.length) {
    return (
      <div
        className="rounded-xl p-5 animate-pulse"
        style={{ backgroundColor: '#111111', border: '1px solid #1f1f1f', height: '260px' }}
      />
    );
  }

  return (
    <div
      className="rounded-xl p-5"
      style={{ backgroundColor: '#111111', border: '1px solid #1f1f1f' }}
    >
      <ResponsiveContainer width="100%" height={240}>
        <LineChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
          <CartesianGrid stroke="#1f1f1f" strokeDasharray="3 3" />
          <XAxis
            dataKey="period"
            tickFormatter={formatPeriod}
            tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 10, fontFamily: 'Inter, sans-serif' }}
            axisLine={{ stroke: '#1f1f1f' }}
            tickLine={false}
          />
          <YAxis
            tickFormatter={formatYAxis}
            tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10, fontFamily: 'Inter, sans-serif' }}
            axisLine={false}
            tickLine={false}
            width={50}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            formatter={(value) => {
              const s = servers?.find(sv => sv?.id === value);
              const color = s?.color || '#888888';
              return (
                <span style={{ color, fontSize: '11px', fontFamily: 'Inter, sans-serif' }}>
                  {value}
                </span>
              );
            }}
          />
          {visibleServers?.map(server => (
            <Line
              key={server?.id}
              type="monotone"
              dataKey={server?.id}
              stroke={server?.color || '#888888'}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, fill: server?.color || '#888888' }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default MonthlyEvolutionChart;
